import React, { useState, useEffect } from 'react';
// Import all service functions
import { listServiceTypes, addServiceType, renameServiceType, deleteServiceType } from '../services/serviceTypeService';
// Added FaEdit, FaTrashAlt
import { FaPlus, FaTools, FaCalendarAlt, FaBarcode, FaTag, FaSave, FaTimes, FaEdit, FaTrashAlt } from 'react-icons/fa';
import ActivityLog from '../components/ActivityLog';

// Helper function to format Firestore Timestamp (similar to AllowedEmailsPage)
const formatTimestamp = (timestamp) => {
  if (!timestamp || !timestamp._seconds) return 'N/A';
  const date = new Date(timestamp._seconds * 1000);
  // Simplified format for just date
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return date.toLocaleDateString('en-AU', options);
};

const ServiceTypesPage = () => {
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // List loading error

  // State for inline add form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newServiceName, setNewServiceName] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // State for Edit Modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [serviceToEdit, setServiceToEdit] = useState(null);
  const [editedServiceName, setEditedServiceName] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // State for Delete Modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Consolidated state for action feedback
  const [actionError, setActionError] = useState(null);
  const [actionSuccessMessage, setActionSuccessMessage] = useState(null);

  const fetchServiceTypes = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listServiceTypes();
      setServiceTypes(data);
    } catch (err) {
      setError(err.message || 'Failed to load service types.');
      setServiceTypes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServiceTypes();
  }, []);

  // Effect to clear action messages
  useEffect(() => {
    let timer;
    if (actionSuccessMessage || actionError) {
      timer = setTimeout(() => {
        setActionSuccessMessage(null);
        setActionError(null);
      }, 5000); // Clear after 5 seconds
    }
    return () => clearTimeout(timer);
  }, [actionSuccessMessage, actionError]);

  // --- Add Handlers ---
  const handleAddServiceClick = () => {
    setShowAddForm(true);
    setNewServiceName('');
    setActionError(null); // Clear general errors
    setActionSuccessMessage(null);
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setNewServiceName('');
    setActionError(null); // Clear general errors
    setActionSuccessMessage(null);
  };

  const handleSaveService = async () => {
    if (!newServiceName.trim()) {
      setActionError("Service name cannot be empty."); // Use general error state
      return;
    }
    setIsAdding(true);
    setActionError(null);
    setActionSuccessMessage(null);

    try {
      const response = await addServiceType(newServiceName);
      setActionSuccessMessage(response.message || `Service '${newServiceName}' added successfully.`);
      setShowAddForm(false);
      setNewServiceName('');
      await fetchServiceTypes(); // Refetch the entire list
    } catch (err) {
      setActionError(err.message || 'Failed to add service.'); // Use general error state
    } finally {
      setIsAdding(false);
    }
  };

  // --- Edit Handlers ---
  const handleEditClick = (service) => {
    setServiceToEdit(service);
    setEditedServiceName(service.name);
    setActionError(null);
    setActionSuccessMessage(null);
    setIsEditModalOpen(true);
  };

  const handleCancelEdit = () => {
    setIsEditModalOpen(false);
    setServiceToEdit(null);
    setEditedServiceName('');
    setActionError(null);
  };

  const handleSaveEdit = async () => {
    if (!serviceToEdit || !editedServiceName.trim()) {
      setActionError("Service name cannot be empty.");
      return;
    }
    if (editedServiceName.trim() === serviceToEdit.name) {
      // Optional: Close modal if name hasn't changed
      handleCancelEdit();
      return;
    }

    setIsEditing(true);
    setActionError(null);
    setActionSuccessMessage(null);

    try {
      const response = await renameServiceType(serviceToEdit.id, editedServiceName);
      setActionSuccessMessage(response.message || `Service renamed successfully.`);
      setIsEditModalOpen(false);
      await fetchServiceTypes(); // Refetch list
    } catch (err) {
      setActionError(err.message || 'Failed to rename service.');
      // Keep modal open on error
    } finally {
      setIsEditing(false);
    }
  };

  // --- Delete Handlers ---
  const handleDeleteClick = (service) => {
    setServiceToDelete(service);
    setActionError(null);
    setActionSuccessMessage(null);
    setIsDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setServiceToDelete(null);
    setActionError(null);
  };

  const handleConfirmDelete = async () => {
    if (!serviceToDelete) return;

    setIsDeleting(true);
    setActionError(null);
    setActionSuccessMessage(null);

    try {
      const response = await deleteServiceType(serviceToDelete.id);
      setActionSuccessMessage(response.message || `Service '${serviceToDelete.name}' deleted successfully.`);
      setIsDeleteModalOpen(false);
      // Remove from state directly for faster UI update, or rely on refetch
      // setServiceTypes(prev => prev.filter(s => s.id !== serviceToDelete.id));
      await fetchServiceTypes(); // Refetch list
    } catch (err) {
      setActionError(err.message || 'Failed to delete service.');
      // Keep modal open on error
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Title Row */}
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-3xl font-bold text-jims-cyan-dark flex items-center">
          <FaTools className="mr-3" /> Service Types
        </h1>
      </div>

      {/* Buttons and Inline Form Area */}
      <div className="mb-6">
        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={handleAddServiceClick}
            className="bg-jims-cyan hover:bg-jims-cyan-dark text-white font-bold py-2 px-4 rounded inline-flex items-center transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={showAddForm || isAdding} // Disable when form is shown or adding
          >
            <FaPlus className="mr-2" />
            Add Service
          </button>
        </div>

        {/* Inline Add Form */}
        {showAddForm && (
          <div className="bg-gray-50 p-4 rounded border border-gray-200 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-grow">
                <label htmlFor="new-service-name" className="sr-only">
                  Service Name
                </label>
                <input
                  type="text"
                  id="new-service-name"
                  value={newServiceName}
                  onChange={(e) => setNewServiceName(e.target.value)}
                  placeholder="Enter new service name"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                  disabled={isAdding}
                />
              </div>
              <div className="flex items-center space-x-2 flex-shrink-0">
                <button
                  onClick={handleSaveService}
                  className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded inline-flex items-center transition duration-300 disabled:opacity-50"
                  disabled={isAdding || !newServiceName.trim()}
                >
                  <FaSave className="mr-2" />
                  {isAdding ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={handleCancelAdd}
                  className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded inline-flex items-center transition duration-300"
                  disabled={isAdding}
                >
                  <FaTimes className="mr-2" />
                  Cancel
                </button>
              </div>
            </div>
            {/* Add Operation Messages */}
            {actionSuccessMessage && (
              <p className="mt-2 text-sm text-green-600">{actionSuccessMessage}</p>
            )}
            {actionError && (
              <p className="mt-2 text-sm text-red-600">Error: {actionError}</p>
            )}
          </div>
        )}
      </div>

      {/* Display Action Messages */}
      {actionSuccessMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded">
          {actionSuccessMessage}
        </div>
      )}
      {actionError && !showAddForm && !isEditModalOpen && !isDeleteModalOpen && ( // Only show general error if no modal/form is active
        <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded">
          Error: {actionError}
        </div>
      )}

      {/* Display List Loading/Error Messages */}
      {loading && <p className="text-center text-gray-500 py-10">Loading service types...</p>}
      {error && <p className="text-center text-red-500 bg-red-100 p-3 rounded mb-4">Error: {error}</p>}

      {/* Service Types Table */}
      {!loading && !error && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          {serviceTypes.length === 0 ? (
            <p className="text-center text-gray-500 p-6">No service types found.</p>
          ) : (
            <table className="min-w-full leading-normal">
              <thead>
                <tr className="bg-gray-100 text-left text-gray-600 uppercase text-sm">
                  <th className="px-5 py-3 border-b-2 border-gray-200">
                    <FaBarcode className="inline mr-1" /> Service Code
                  </th>
                  <th className="px-5 py-3 border-b-2 border-gray-200">
                    <FaTag className="inline mr-1" /> Service Name
                  </th>
                  <th className="px-5 py-3 border-b-2 border-gray-200">
                    <FaCalendarAlt className="inline mr-1" /> Date Added
                  </th>
                  {/* Added Actions Header */}
                  <th className="px-5 py-3 border-b-2 border-gray-200 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {serviceTypes.map((service) => {
                  const isCurrentEditingOrDeleting = (serviceToEdit?.id === service.id && isEditing) || (serviceToDelete?.id === service.id && isDeleting);
                  const disableActions = isAdding || isEditing || isDeleting;

                  return (
                    <tr key={service.id} className="hover:bg-gray-50">
                      <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                        <p className="text-gray-900 whitespace-no-wrap">{service.serviceTypeCode || 'N/A'}</p>
                      </td>
                      <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                        <p className="text-gray-900 whitespace-no-wrap">{service.name || 'N/A'}</p>
                      </td>
                      <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                        <p className="text-gray-900 whitespace-no-wrap">{formatTimestamp(service.createdAt)}</p>
                      </td>
                      {/* Actions Cell */}
                      <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm text-center">
                        <button
                          onClick={() => handleEditClick(service)}
                          className="text-blue-500 hover:text-blue-700 mr-3 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label={`Edit service ${service.name}`}
                          disabled={disableActions || isCurrentEditingOrDeleting}
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(service)}
                          className="text-red-500 hover:text-red-700 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label={`Delete service ${service.name}`}
                          disabled={disableActions || isCurrentEditingOrDeleting}
                        >
                          <FaTrashAlt />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Edit Service Modal */}
      {isEditModalOpen && serviceToEdit && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div className="relative mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Edit Service Name</h3>
            <div className="mb-4">
              <label htmlFor="edit-service-name" className="block text-sm font-medium text-gray-700">
                Service Name
              </label>
              <input
                type="text"
                id="edit-service-name"
                value={editedServiceName}
                onChange={(e) => setEditedServiceName(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm"
                required
                disabled={isEditing}
              />
            </div>
            {/* Edit Modal Error */}
            {actionError && (
              <p className="text-sm text-red-600 mb-3">{actionError}</p>
            )}

            <ActivityLog
              entityType="serviceType"
              entityId={serviceToEdit.id}
              createdAt={serviceToEdit.createdAt}
              createdLabel="Service type created"
            />

            <div className="flex justify-end space-x-3 mt-4">
              <button
                type="button"
                onClick={handleCancelEdit}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition duration-150"
                disabled={isEditing}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition duration-150 disabled:opacity-50"
                disabled={isEditing || !editedServiceName.trim() || editedServiceName.trim() === serviceToEdit.name}
              >
                {isEditing ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && serviceToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
          <div className="relative mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-2">Confirm Deletion</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete the service type <span className="font-semibold">{serviceToDelete.name}</span> (Code: {serviceToDelete.serviceTypeCode})? This action cannot be undone.
            </p>
            {/* Delete Modal Error */}
            {actionError && (
              <p className="text-sm text-red-600 mb-3">{actionError}</p>
            )}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancelDelete}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition duration-150"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition duration-150 disabled:opacity-50"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceTypesPage;
